import {useEffect, useRef, useState} from "react";
import {useFetch} from "hooks/useFetch";
import useIsFocused from "hooks/useIsFocused";
import {CUSTOMER_SHIFTS_TABS} from "constants/constants";
import {
    buildActivePoolShiftParams,
    buildDatedPoolShiftParams,
    buildShiftCountParams,
    createCompletedDateFilters,
    createPlannedDateFilters,
    normalizePoolShiftList,
    normalizeShiftCounts
} from "../utils/customerShiftsPageUtils";

const useCustomerShiftsPage = ({poolShiftStore, selectedSubCompanyId, isDedicatedIntegration}) => {
    const [tabKey, setTabKey] = useState(CUSTOMER_SHIFTS_TABS.ACTIVE);
    const [activeCurrentPage, setActiveCurrentPage] = useState(1);
    const [plannedCurrentPage, setPlannedCurrentPage] = useState(1);
    const [completedCurrentPage, setCompletedCurrentPage] = useState(1);
    const [reloadToken, setReloadToken] = useState(false);
    const [recall, setRecall] = useState(true);
    const isFocused = useIsFocused();

    const [completedFilters, setCompletedFilters] = useState(() =>
        createCompletedDateFilters(selectedSubCompanyId)
    );
    const [plannedFilters, setPlannedFilters] = useState(() =>
        createPlannedDateFilters(selectedSubCompanyId)
    );

    const recallRef = useRef(recall);
    recallRef.current = recall;

    const canFetch = isDedicatedIntegration && !!selectedSubCompanyId;
    const isActiveTab = tabKey === CUSTOMER_SHIFTS_TABS.ACTIVE;
    const isPlannedTab = tabKey === CUSTOMER_SHIFTS_TABS.PLANNED;
    const isCompletedTab = tabKey === CUSTOMER_SHIFTS_TABS.COMPLETED;

    useEffect(() => {
        if (!selectedSubCompanyId) return;

        setCompletedFilters(prev => ({
            ...prev,
            subCompany: [selectedSubCompanyId]
        }));
        setPlannedFilters(prev => ({
            ...prev,
            subCompany: [selectedSubCompanyId]
        }));
    }, [selectedSubCompanyId]);

    useEffect(() => {
        if (!isFocused) return;

        const timeout = setTimeout(() => {
            setRecall(current => !current);
        }, 30000);

        return () => clearTimeout(timeout);
    }, [recall, isFocused]);

    const [, shiftCounts] = useFetch({
        defaultDataValue: {activeCount: 0, plannedCount: 0, completedCount: 0},
        fetchFn: poolShiftStore.getPoolShiftCountsByForecastDate.bind(
            null,
            buildShiftCountParams(
                selectedSubCompanyId,
                tabKey,
                plannedFilters,
                completedFilters
            )
        ),
        dependencies: [
            selectedSubCompanyId,
            reloadToken,
            recall,
            tabKey,
            plannedFilters,
            completedFilters
        ],
        condition: canFetch,
        transform: normalizeShiftCounts
    });

    const [activeLoading, activeData] = useFetch({
        defaultDataValue: {rows: [], dataCount: 0},
        fetchFn: poolShiftStore.getActivePoolShiftByForecastDate.bind(
            null,
            buildActivePoolShiftParams(selectedSubCompanyId, activeCurrentPage)
        ),
        dependencies: [selectedSubCompanyId, reloadToken, recall, activeCurrentPage],
        condition: canFetch && isActiveTab,
        transform: normalizePoolShiftList
    });

    const [completedLoading, completedData] = useFetch({
        defaultDataValue: {rows: [], dataCount: 0},
        fetchFn: poolShiftStore.getCompletedPoolShiftByForecastDate.bind(
            null,
            buildDatedPoolShiftParams(selectedSubCompanyId, completedFilters, completedCurrentPage)
        ),
        dependencies: [
            completedFilters,
            reloadToken,
            recall,
            selectedSubCompanyId,
            completedCurrentPage
        ],
        condition: canFetch && isCompletedTab,
        transform: normalizePoolShiftList
    });

    const [plannedLoading, plannedData] = useFetch({
        defaultDataValue: {rows: [], dataCount: 0},
        fetchFn: poolShiftStore.getPlannedPoolShiftByForecastDate.bind(
            null,
            buildDatedPoolShiftParams(selectedSubCompanyId, plannedFilters, plannedCurrentPage)
        ),
        dependencies: [plannedFilters, reloadToken, recall, selectedSubCompanyId, plannedCurrentPage],
        condition: canFetch && isPlannedTab,
        transform: normalizePoolShiftList
    });

    const changeTab = nextTab => {
        setTabKey(nextTab);
        setActiveCurrentPage(1);
        setPlannedCurrentPage(1);
        setCompletedCurrentPage(1);

        if (nextTab === CUSTOMER_SHIFTS_TABS.COMPLETED) {
            setCompletedFilters(createCompletedDateFilters(selectedSubCompanyId));
        }

        if (nextTab === CUSTOMER_SHIFTS_TABS.PLANNED) {
            setPlannedFilters(createPlannedDateFilters(selectedSubCompanyId));
        }
    };

    return {
        tabKey,
        changeTab,
        shiftCounts,
        activeCurrentPage,
        setActiveCurrentPage,
        plannedCurrentPage,
        setPlannedCurrentPage,
        completedCurrentPage,
        setCompletedCurrentPage,
        completedFilters,
        setCompletedFilters,
        plannedFilters,
        setPlannedFilters,
        activeTab: {
            loading: activeLoading,
            data: activeData
        },
        plannedTab: {
            loading: plannedLoading,
            data: plannedData
        },
        completedTab: {
            loading: completedLoading,
            data: completedData
        }
    };
};

export default useCustomerShiftsPage;
